<template>
    <div class="card-form">
        <span
            v-if="formError"
            class="form-error form-global-error"
        >
            {{ formError }}
        </span>
        <div v-show="!hideCard" ref="card" />
        <span
            v-if="cardError"
            class="form-error"
        >
            {{ cardError }}
        </span>
        <div
            :class="[ !update ? 'card-payment' : '', 'payment' ]"
        >
            <template v-if="!update">
                <coupon-code
                    :initial-code="initialCouponCode"
                    :billing-plan="billingPlan"
                    @change="changeCouponCode"
                />
                <base-submit-button
                    :disabled="cardError || formError"
                    class="btn"
                    :loading="formProcessing"
                    @click="processUserSubscription"
                >
                    Confirm and Subscribe
                </base-submit-button>
            </template>
            <template v-else>
                <base-submit-button
                    :disabled="cardError || formError"
                    class="btn btn-update"
                    :loading="formProcessing || externalFormProcessing"
                    @click="processPaymentMethodUpdate"
                >
                    <slot name="update-submit-btn">
                        Save changes
                    </slot>
                </base-submit-button>
                <button
                    class="btn btn-update btn-secondary"
                    @click="$emit('cancel')"
                >
                    <slot name="update-cancel-btn">
                        Cancel
                    </slot>
                </button>
            </template>
        </div>
    </div>
</template>

<script>
/* global Stripe */

import style from '@/utils/form/stripeElements';
import { loadScript } from '@/utils/scriptLoader';
import CouponCode from '@/components/Billing/CouponCode';
import apiCall from '@/utils/apiCall';
import toasts from '@/mixins/toasts';
import { mapState, mapActions } from 'vuex';

export default {
    components: {
        CouponCode,
    },

    mixins: [toasts],

    props: {
        update: {
            type: Boolean,
            default: false,
        },
        externalFormProcessing: {
            type: Boolean,
            default: false,
        },
        upgradeUser: {
            type: Boolean,
            default: false,
        },
        activeSites: {
            type: Number,
            default: 1,
        },
        subscribeCardError: {
            type: String,
            default: '',
        },
        initialCouponCode: {
            type: String,
            default: null,
        },
        billingPlan: {
            required: true,
            type: [Object, Boolean],
        },
    },

    data: () => ({
        cardError: null,
        stripeSecret: null,
        formProcessing: false,
        formError: null,
        hideCard: false,
        couponCode: null,
        stripe: {
            instance: null,
            card: null,
            elements: null,
        },
    }),

    computed: {
        ...mapState(['user', 'userSubscription']),
    },

    watch: {
        /**
         * Watch for card error during API subscription process
         *
         * @param   {string}  error  API subscription error.
         *
         */
        subscribeCardError(error) {
            this.cardError = error;
        },
    },

    /**
     * Mounted hook
     */
    mounted() {
        this.getStripeSecret();

        loadScript('js.stripe.com/v3/', 'stripe', function () {
            this.configureStripe();
        }.bind(this));
    },

    methods: {
        ...mapActions([
            'loadUser',
            'loadLicenses',
            'reloadPaymentMethods',
            'loadUserSubscription',
        ]),

        /**
         * Gets secret
         */
        async getStripeSecret() {
            const response = await apiCall(this, 'get', `${this.$appUrl}/api/billing/intent`);
            const { status, data } = response;
            if (200 === status) {
                this.stripeSecret = data.client_secret;
            } else {
                this.hideCard = true;
                this.formError = data.message;
            }
        },

        /**
         * Configures stripe
         */
        configureStripe() {
            this.stripe.instance = Stripe(process.env.MIX_STRIPE_KEY);
            this.stripe.elements = this.stripe.instance.elements();
            this.stripe.card = this.stripe.elements.create('card', style);
            this.stripe.card.mount(this.$refs.card);

            this.validateCard();
        },

        /**
         * Validates card data
         *
         */
        validateCard() {
            this.stripe.card.addEventListener('change', (e) => {
                if (e.error) {
                    this.cardError = e.error.message;
                    this.formProcessing = false;
                } else {
                    this.cardError = null;
                }
            });
        },

        /**
         * Gets Stripe setup intent
         *
         * @returns {Promise} Stripe Setup Intent
         */
        getSetupIntent() {
            const payment = {
                payment_method: {
                    card: this.stripe.card,
                    billing_details: this.user.name,
                },
            };

            this.formProcessing = true;

            return this.stripe.instance.confirmCardSetup(this.stripeSecret, payment);
        },

        /**
         * Processes user subscription
         */
        async processUserSubscription() {
            await apiCall(this, 'put', '/api/user/meta', { payment_initialized: Date.now() });

            const { setupIntent, error } = await this.getSetupIntent();

            if (error) {
                this.formProcessing = false;
                this.cardError = error.message;
            } else {
                this.$emit('stripe-success', setupIntent.payment_method, this.couponCode);
            }
        },

        /**
         * Processes update card request
         */
        async processPaymentMethodUpdate() {
            const { setupIntent, error } = await this.getSetupIntent();

            if (error) {
                this.formProcessing = false;
                this.toastError({
                    message: {
                        title: error.message,
                    },
                });
            } else {
                this.$emit('stripe-success', setupIntent.payment_method);
            }
        },

        /**
         * Changes coupon code.
         *
         * @param  {string|null} couponCode Coupon code.
         */
        changeCouponCode(couponCode) {
            this.couponCode = couponCode;
        },
    },
};
</script>
